import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Coffee, Home, UtensilsCrossed, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { logQueueService } from '@/services/logQueue';
import { gpsService } from '@/services/gps';
import { useBlockBack } from '@/lib/useBlockBack';
import { LogTypes } from '@/types/domain';

type StatusKey = 'meal' | 'coffee' | 'base';

interface StatusConfig {
  title: string;
  subtitle: string;
  endLabel: string;
  startLog: string;
  endLog: string;
  icon: LucideIcon;
  stopGps?: boolean;
}

const CONFIG: Record<StatusKey, StatusConfig> = {
  meal: {
    title: 'ON MEAL BREAK',
    subtitle: 'Tap below when you are done',
    endLabel: 'END MEAL',
    startLog: LogTypes.mealStart,
    endLog: LogTypes.mealEnd,
    icon: UtensilsCrossed,
  },
  coffee: {
    title: 'ON COFFEE BREAK',
    subtitle: 'Tap below when you are done',
    endLabel: 'END BREAK',
    startLog: LogTypes.coffeeStart,
    endLog: LogTypes.coffeeEnd,
    icon: Coffee,
  },
  base: {
    title: 'RETURNING TO TERMINAL',
    subtitle: 'Tap below when you arrive at the terminal',
    endLabel: 'ARRIVED AT TERMINAL',
    startLog: LogTypes.backToBase,
    endLog: LogTypes.arrivedAtTerminal,
    icon: Home,
    stopGps: true,
  },
};

/**
 * Full-screen status takeover shown while the driver is on a meal/coffee break
 * or returning to the terminal. Mirrors the native StatusModeScreen: the start
 * log fires on entry, the only way out is the explicit end button (back is
 * blocked), and the end log fires on exit.
 */
export function StatusModePage() {
  const { mode } = useParams<{ mode: StatusKey }>();
  const navigate = useNavigate();
  const cfg = mode ? CONFIG[mode] : undefined;
  const startedAt = useRef<number>(Date.now());
  const startedLog = useRef(false);
  const [ending, setEnding] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!cfg || startedLog.current) return;
    startedLog.current = true;
    startedAt.current = Date.now();
    void (async () => {
      if (cfg.stopGps) await gpsService.stopRouteTracking();
      await logQueueService.enqueue({ logType: cfg.startLog, display: 'Display' });
    })();
  }, [cfg]);

  // Block back navigation while on break (parity with native PopScope).
  useBlockBack(true);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Date.now() - startedAt.current), 1000);
    return () => clearInterval(t);
  }, []);

  if (!cfg) {
    return <div className="p-6 text-brand-muted">Unknown status.</div>;
  }

  const end = async () => {
    if (ending) return;
    setEnding(true);
    await logQueueService.enqueue({ logType: cfg.endLog, display: 'Display' });
    navigate('/main', { replace: true });
  };

  const mins = Math.floor(elapsed / 60000);
  const secs = Math.floor((elapsed % 60000) / 1000);
  const Icon = cfg.icon;

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-8 bg-brand-bg px-6 py-10 text-center">
      <Icon className="h-20 w-20 text-brand-primaryBlue" />
      <div>
        <h1 className="text-2xl font-extrabold tracking-wide">{cfg.title}</h1>
        <p className="mt-2 text-brand-muted">{cfg.subtitle}</p>
        <p className="mt-4 text-4xl font-mono font-bold tabular-nums">
          {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </p>
      </div>
      <Button
        variant="danger"
        size="xl"
        fullWidth
        className="max-w-sm"
        disabled={ending}
        onClick={() => void end()}
      >
        {cfg.endLabel}
      </Button>
    </div>
  );
}
